import config from '../config';
import selectedEnvironment from '../env';
import { deriveBusinessTotalsFromVatDocuments } from '../Utils/advanceCalculator/advanceCalculatorSelectors';

const API_URL = `${config[selectedEnvironment].API_URL}/ClientDetailsPage`;

const fetchVatAdvancesPageData = async (dto, token) => {
    const response = await fetch(`${API_URL}/GetVatAdvancesPageData`, {
        method: 'POST',
        headers: {
            "Authorization": 'Bearer ' + token,
            "Content-Type": "application/json"
        },
        body: JSON.stringify(dto)
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
    }
    return response.json();
};

const fetchStatusOverviewChartData = async (dto, token) => {
    const response = await fetch(`${API_URL}/GetStatusOverviewChartData`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
    }
    return response.json();
};

/**
 * Transforms the consolidated API response into the shape expected by setAllClientData.
 * Uses advance-period-specific values (advanceIncomesNoVat, advanceReceiptWithholding)
 * for the advance table, which may differ from the VAT period values.
 */
const buildReduxPayload = (data, client, user) => {
    const income = data.income || {};
    const expense = data.expense || {};
    const equipment = data.equipment || {};

    const incomeVat = income.vat || 0;
    const expenseVat = expense.vat || 0;
    const equipmentVat = equipment.vat || 0;
    const netVatAmount = incomeVat - expenseVat - equipmentVat;

    const {
        incomesWithoutVat,
        expensesWithoutVat,
        equipmentWithoutVat,
        profitAmountVatParity: profitAmount,
    } = deriveBusinessTotalsFromVatDocuments(income, expense, equipment);

    const zeroVatAmount = data.zeroVatAmount || 0;
    const sellEquipNoVat = data.sellEquipmentWithoutVat || 0;

    const reportTax = data.reportedVat
        ? {
            vatTransactions: data.reportedVat.vatTransactions ?? 0,
            otherVATTaxes: data.reportedVat.otherVATTaxes ?? 0,
            equipmentVAT: data.reportedVat.equipmentVAT ?? 0,
            netVAT: data.reportedVat.netVAT ?? 0,
            ...data.reportedVat
        }
        : { vatTransactions: 0, otherVATTaxes: 0, equipmentVAT: 0, netVAT: 0 };

    const advanceReportTax = data.reportedAdvance || {};

    return {
        user: {
            accountantId: user.accountantId,
            caseId: client.caseId
        },
        reportTax,
        advanceReportTax,
        income,
        expense,
        equipment,
        zeroVatAmount,
        sellEquipNoVat,
        balances: data.balance || 0,
        incomeTaxAdvances: data.incomeTaxAdvances || 0,
        withholding: {
            payment: data.paymentWithholding || 0,
            accepted: data.advanceReceiptWithholding || 0
        },
        closedMonths: {
            start: data.isStartMonthClosed || false,
            end: data.isEndMonthClosed || false
        },
        incomesVatDetailed: income,
        incomesAmountDetailed: {
            amount17: income.totalWithoutVat17 || 0,
            amount18: income.totalWithoutVat18 || 0
        },
        expensesVatDetailed: expense,
        equipmentVatDetailed: equipment,
        incomesVatAmount: incomeVat,
        expensesVatAmount: expenseVat,
        purchaseOfEquipmentExpensesVatAmount: equipmentVat,
        incomesAmount: incomesWithoutVat,
        expensesAmount: expensesWithoutVat,
        purchaseOfEquipmentExpensesAmount: equipmentWithoutVat,
        profitAmount,
        netVatAmount,
        incomeNoVat: zeroVatAmount,
        witholdingIncomesNoVat: data.advanceIncomesNoVat || 0,
        paymentWithholdingTax: data.paymentWithholding || 0,
        // Resolver-based advance settings for the active advance period (yearly row vs legacy client fields)
        pageIncomeTaxAdvancePercentage: data.incomeTaxAdvancePercentage,
        pageUseIncomeTaxAdvancePercentage: data.useIncomeTaxAdvancePercentage,
        pageIncomeTaxAdvanceAmount: data.incomeTaxAdvanceAmount,
        // Per-partner per-year resolved settings for partnership clients (keyed by partner Guid string).
        // Empty object when client is not a partnership (or backend is older and omits the field).
        partnerAdvanceSettings: data.partnerAdvanceSettings || {},
    };
};

const VatAdvancesDataService = {
    fetchVatAdvancesPageData,
    fetchStatusOverviewChartData,
    buildReduxPayload,
};

export default VatAdvancesDataService;
